import { useCallback } from "react";
import { ScenarioModel } from "../../clients/KompasApi";
import {
  Collapse,
  Flex,
  Table,
  Tbody,
  Td,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import {
  highlightDifferencesAtom,
  onlyShowDifferencesAtom,
} from "../../pages/ComparePage";
import { AvailibilityIcon } from "../../elements/AvailibilityIcon";
import {
  extractValue,
  hasAnyMeasure,
  hasMeasure,
} from "../../shared/utils/measureUtils";
import { CategoryHeader } from "./CategoryHeader";
import { CategoryFooter } from "./CategoryFooter";
import { TaxKey } from "../TaxSelect";
import { useComparePageContext } from "../../shared/state/ComparePageContext";

export const tempCalculateCO2Impact = (scenario: ScenarioModel) =>
  scenario.calculation.yearlyCO2Emission / (scenario.glazing.id ?? 1);
export const tempCalculatePrice = (scenario: ScenarioModel) =>
  (scenario.glazing.id ?? 0) * 100;

export function FrameMeasuresSubTable() {
  const { scenarios, situation } = useComparePageContext();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  const [onlyShowDifferences] = useAtom(onlyShowDifferencesAtom);
  const [highlightDifferences] = useAtom(highlightDifferencesAtom);

  const scenarioValuesDiffer = useCallback(
    (measure: keyof ScenarioModel) =>
      new Set(scenarios.map((scenario) => extractValue(scenario[measure])))
        .size > 1,
    [scenarios],
  );

  return (
    <Collapse
      in={hasAnyMeasure(
        ["glazing", "exteriorDoors", "hasFramePanels", "frame"],
        scenarios,
        situation,
        onlyShowDifferences,
      )}
    >
      <Table
        className={isOpen ? "open" : "closed"}
        sx={{ borderSpacing: "2rem 0" }}
        variant="compare"
      >
        <CategoryHeader
          isOpen={isOpen}
          onToggle={onToggle}
        >
          Kozijnen
        </CategoryHeader>
        <Tbody>
          {hasMeasure("frame", scenarios, situation, onlyShowDifferences) && (
            <Tr
              className={
                highlightDifferences && scenarioValuesDiffer("frame")
                  ? "highlight"
                  : ""
              }
              data-id="measure-frame"
            >
              <Td>Type kozijn</Td>
              <Td textAlign="center">-</Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  {scenario.frame?.name}
                </Td>
              ))}
            </Tr>
          )}
          {hasMeasure("glazing", scenarios, situation, onlyShowDifferences) && (
            <Tr
              className={
                highlightDifferences && scenarioValuesDiffer("glazing")
                  ? "highlight"
                  : ""
              }
              data-id="measure-glazing"
            >
              <Td>Type beglazing</Td>
              <Td textAlign="center">{situation.glazing?.name}</Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  {scenario.glazing?.name}
                </Td>
              ))}
            </Tr>
          )}
          {hasMeasure(
            "exteriorDoors",
            scenarios,
            situation,
            onlyShowDifferences,
          ) && (
            <Tr
              className={
                highlightDifferences && scenarioValuesDiffer("exteriorDoors")
                  ? "highlight"
                  : ""
              }
              data-id="measure-exteriorDoors"
            >
              <Td>Type buitendeuren</Td>
              <Td textAlign="center">-</Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  {scenario.exteriorDoors?.name}
                </Td>
              ))}
            </Tr>
          )}
          {hasMeasure(
            "hasFramePanels",
            scenarios,
            situation,
            onlyShowDifferences,
          ) && (
            <Tr
              className={
                highlightDifferences && scenarioValuesDiffer("hasFramePanels")
                  ? "highlight"
                  : ""
              }
              data-id="measure-hasFramePanels"
            >
              <Td>Panelen in kozijn</Td>
              <Td textAlign="center">
                <Flex placeContent="center">
                  <AvailibilityIcon available={situation.hasFramePanels} />
                </Flex>
              </Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  <Flex placeContent="center">
                    <AvailibilityIcon available={scenario.hasFramePanels} />
                  </Flex>
                </Td>
              ))}
            </Tr>
          )}
        </Tbody>
        <CategoryFooter
          calculateCO2Impact={tempCalculateCO2Impact}
          calculatePrice={tempCalculatePrice}
          taxKey={TaxKey.Frame}
        />
      </Table>
    </Collapse>
  );
}
